import {
	Box,
	Button,
	CardActions,
	CardContent,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { AchievementTreasureCondition } from '@/api/enum/common.enum';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { API_DATE_TIME_FORMAT, isTime } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import { TaskAchievementCumulativeSeriesDTO } from '@/api/DTO/task/achievement.interface';
import { useUpdateAchievementTreasureDetailMutation } from '@/api/task/treasureApiSlice';
import AppDateTimePicker from '@/common/components/form-inputs/AppDateTimePicker';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
	type: FormType;
	details?: TaskAchievementCumulativeSeriesDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function TreasureInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [updateAchievementTreasureDetail] =
		useUpdateAchievementTreasureDetailMutation();

	const conditionList = [
		{
			id: AchievementTreasureCondition.Gallery,
			name: t('achievementList.Gallery'),
		},
		{
			id: AchievementTreasureCondition.Puzzle,
			name: t('achievementList.Puzzle'),
		},
		{
			id: AchievementTreasureCondition.Email,
			name: t('achievementList.Email'),
		},
		{
			id: AchievementTreasureCondition.Card,
			name: t('achievementList.Card'),
		},
		{
			id: AchievementTreasureCondition.Chartlet,
			name: t('achievementList.Chartlet'),
		},
	];

	const defaultValues: DefaultValues<TaskAchievementCumulativeSeriesDTO> = {};
	useEffect(() => {
		if (type == FormType.edit) {
			if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
			if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
			if (details?.finishedConditionName)
				methods.setValue(
					'finishedConditionName',
					details.finishedConditionName
				);
			if (details?.publishedStartAt)
				methods.setValue('publishedStartAt', details.publishedStartAt);
			if (details?.publishedEndAt)
				methods.setValue('publishedEndAt', details.publishedEndAt);
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		publishedStartAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		publishedEndAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		titleZh: Yup.string().required(t('errorMessage.required')),
	});
	const methods = useForm<TaskAchievementCumulativeSeriesDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<TaskAchievementCumulativeSeriesDTO> = async (
		data
	) => {
		const response = await updateAchievementTreasureDetail({
			...data,
			id: details?.id || '',
		});
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createMeditationSuccess')
					: t('snackbar.editMeditationSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/task-manage/achievement-list');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/task-manage/achievement-list/treasureId');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('achievementList.seriesName')}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('achievementList.seriesName')}
											maxLength={100}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppTextField
									disabled
									control={methods.control}
									name='finishedConditionName'
									label={t('achievementList.completionConditionsApp')}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedStartAt'
									label={t('taskManage.pushedTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedEndAt'
									label={t('taskManage.pulledTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={6}>
								<Box
									display='flex'
									justifyContent='space-between'
									alignItems='center'
								>
									<Typography
										variant='body2'
										sx={{
											display: 'flex',
										}}
									>
										<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
										&nbsp;
										{t('taskManage.tips')}
									</Typography>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.add')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
